import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { matchesAPI, teamsAPI } from '../services/api';
import toast from 'react-hot-toast';
import ErrorReviewPanel from '../components/ErrorReviewPanel';
import ErrorStatisticsPanel from '../components/ErrorStatisticsPanel';

const EVENT_TYPES = ['serve', 'spike', 'block', 'set', 'dig', 'receive'];

function ReviewPage() {
  const { matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [roster, setRoster] = useState([]);
  const [events, setEvents] = useState([]);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [corrections, setCorrections] = useState([]);
  const [ballTracks, setBallTracks] = useState([]);
  const [showCorrectionsHistory, setShowCorrectionsHistory] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [activeTab, setActiveTab] = useState('events'); // 'events' or 'errors'
  const playerRef = useRef(null);

  useEffect(() => {
    fetchMatchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  const fetchMatchData = async () => {
    try {
      // Get match data first
      const matchData = await matchesAPI.getMatch(matchId);
      setMatch(matchData);

      // Get team roster (players)
      if (matchData.team_id) {
        const rosterData = await teamsAPI.getRoster(matchData.team_id);
        setRoster(rosterData);
      }

      // Get unverified events
      const eventsData = await matchesAPI.getEvents(matchId, false);
      setEvents(eventsData.filter(e => !e.verified));

      // Get event corrections history
      try {
        const correctionsData = await matchesAPI.getEventCorrections(matchId);
        setCorrections(correctionsData.corrections || []);
      } catch (error) {
        console.log('Could not fetch corrections (may not be available yet)');
      }

      // Get ball tracks
      try {
        const ballTracksData = await matchesAPI.getBallTracks(matchId);
        setBallTracks(ballTracksData.tracks || []);
      } catch (error) {
        console.log('Could not fetch ball tracks');
      }
    } catch (error) {
      console.error('Failed to fetch match data:', error);
      toast.error('Failed to load match data');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkPlayer = async (eventId, playerId) => {
    try {
      await matchesAPI.linkEventToPlayer(matchId, eventId, playerId);

      // Update local state
      const updatedEvent = events.find(e => e.id === eventId);
      if (updatedEvent) {
        updatedEvent.player_profile_id = playerId;
      }

      // Move to next event
      if (currentEventIndex < events.length - 1) {
        setCurrentEventIndex(currentEventIndex + 1);
      } else if (currentEventIndex > 0) {
        setCurrentEventIndex(currentEventIndex - 1);
      }

      toast.success('Player linked successfully');
    } catch (error) {
      console.error('Failed to link player:', error);
      toast.error('Failed to link player');
    }
  };

  const handleCorrectEventType = async (eventId, newEventType) => {
    try {
      await matchesAPI.correctEventType(matchId, eventId, newEventType, correctionReason);

      // Update local state
      const updatedEvent = events.find(e => e.id === eventId);
      if (updatedEvent) {
        updatedEvent.event_type = newEventType;
      }

      // Reset form
      setCorrectionReason('');
      setSelectedEventType(null);

      // Move to next event
      if (currentEventIndex < events.length - 1) {
        setCurrentEventIndex(currentEventIndex + 1);
      } else if (currentEventIndex > 0) {
        setCurrentEventIndex(currentEventIndex - 1);
      }

      // Refresh corrections history
      const correctionsData = await matchesAPI.getEventCorrections(matchId);
      setCorrections(correctionsData.corrections || []);

      toast.success('Event type corrected');
    } catch (error) {
      console.error('Failed to correct event:', error);
      toast.error('Failed to correct event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      // Note: This would need a backend endpoint to delete events
      setEvents(events.filter(e => e.id !== eventId));

      if (currentEventIndex >= events.length - 1 && currentEventIndex > 0) {
        setCurrentEventIndex(currentEventIndex - 1);
      }

      toast.success('Event deleted');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const getEventTypeForCorrection = (eventId) => {
    const correction = corrections.find(c => c.event_id === eventId);
    return correction ? correction.corrected_type : null;
  };

  const seekToEvent = (event) => {
    if (playerRef.current) {
      playerRef.current.seekTo(event.timestamp, 'seconds');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <div className="loading">Loading match data...</div>;
  }

  const currentEvent = events[currentEventIndex];

  if (!match) {
    return <div className="loading">Match not found</div>;
  }

  return (
    <div className="review-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1>{match.title}</h1>
          <p style={{ color: '#64748b' }}>Review and verify detected events</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link to={`/matches`} className="btn btn-secondary">
            ← Back to Matches
          </Link>
        </div>
      </div>

      {match.status !== 'review' && match.status !== 'completed' && (
        <div className="card" style={{ background: '#fef3c7', marginBottom: '2rem' }}>
          <p><strong>Status:</strong> {match.status}</p>
          <p>Video is still processing. Please wait...</p>
        </div>
      )}

      <div className="grid grid-2">
        {/* Video Player */}
        <div>
          <div className="card">
            <h3 style={{ marginBottom: '1rem' }}>Video Player</h3>
            <div className="video-container">
              <ReactPlayer
                ref={playerRef}
                url={matchesAPI.getVideoUrl(matchId)}
                controls
                width="100%"
                height="400px"
              />
            </div>
            
            {currentEvent && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>Current Event:</strong> {currentEvent.event_type}
                    <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
                      Time: {formatTime(currentEvent.timestamp)}
                    </div>
                  </div>
                  <button onClick={() => seekToEvent(currentEvent)} className="btn btn-secondary">
                    ⏯ Jump to Event
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Team Roster */}
          <div className="card" style={{ marginTop: '1rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Team Roster</h3>
            {roster.length === 0 ? (
              <p style={{ color: '#64748b' }}>No players on roster.</p>
            ) : (
              <div className="grid grid-2">
                {roster.map(player => (
                  <div key={player.id} style={{ padding: '0.5rem', background: '#f9fafb', borderRadius: '4px' }}>
                    <strong>#{player.jersey_number}</strong> {player.name || 'Unknown'}
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                      {player.position}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Review Tabs */}
        <div>
          {/* Tab Buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <button
              onClick={() => setActiveTab('events')}
              className={activeTab === 'events' ? 'btn btn-primary' : 'btn btn-secondary'}
            >
              📋 Events Review ({events.length})
            </button>
            <button
              onClick={() => setActiveTab('errors')}
              className={activeTab === 'errors' ? 'btn btn-primary' : 'btn btn-secondary'}
            >
              ⚠️ Error Review
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={activeTab === 'stats' ? 'btn btn-primary' : 'btn btn-secondary'}
            >
              📊 Error Statistics
            </button>
          </div>

          {/* Events Tab */}
          {activeTab === 'events' && (
            <>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h3>Event Review ({events.length} unverified)</h3>
                {corrections.length > 0 && (
                  <button
                    onClick={() => setShowCorrectionsHistory(!showCorrectionsHistory)}
                    style={{ fontSize: '0.875rem', color: '#0ea5e9', cursor: 'pointer', marginTop: '0.5rem' }}
                  >
                    📋 View Corrections History ({corrections.length})
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setCurrentEventIndex(Math.max(0, currentEventIndex - 1))}
                  className="btn btn-secondary"
                  disabled={currentEventIndex === 0}
                >
                  ← Prev
                </button>
                <button
                  onClick={() => setCurrentEventIndex(Math.min(events.length - 1, currentEventIndex + 1))}
                  className="btn btn-secondary"
                  disabled={currentEventIndex >= events.length - 1}
                >
                  Next →
                </button>
              </div>
            </div>

            {events.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                <h3>All events verified!</h3>
                <p>No unverified events remaining.</p>
                <Link to={`/matches/${matchId}/stats`} className="btn btn-primary" style={{ marginTop: '1rem' }}>
                  View Statistics
                </Link>
              </div>
            ) : currentEvent && (
              <>
                <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '4px', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    Event #{currentEventIndex + 1} of {events.length}
                  </div>
                  <div style={{ color: '#64748b', marginTop: '0.5rem' }}>
                    Timestamp: {formatTime(currentEvent.timestamp)}
                  </div>
                  <div style={{ color: '#64748b' }}>
                    AI Detected Type: <strong>{currentEvent.event_type}</strong>
                  </div>
                  <div style={{ color: '#64748b' }}>
                    Confidence: {(currentEvent.confidence * 100).toFixed(0)}%
                  </div>
                  {currentEvent.player_profile_id && (
                    <div style={{ color: '#64748b' }}>
                      Assigned to: <strong>{currentEvent.player_profile_id}</strong>
                    </div>
                  )}
                </div>

                {/* Correct Event Type */}
                <div style={{ marginBottom: '1rem', padding: '1rem', background: '#eff6ff', borderRadius: '4px' }}>
                  <strong>Correct Event Type (if AI was wrong):</strong>
                  <div style={{ marginTop: '0.5rem' }}>
                    <select
                      value={selectedEventType || ''}
                      onChange={(e) => setSelectedEventType(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #cbd5e1',
                        marginBottom: '0.5rem'
                      }}
                    >
                      <option value="">-- Select a different event type --</option>
                      {EVENT_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {selectedEventType && (
                      <>
                        <textarea
                          value={correctionReason}
                          onChange={(e) => setCorrectionReason(e.target.value)}
                          placeholder="Why are you correcting this? (optional)"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            border: '1px solid #cbd5e1',
                            marginBottom: '0.5rem',
                            fontFamily: 'inherit'
                          }}
                          rows={2}
                        />
                        <button
                          onClick={() => handleCorrectEventType(currentEvent.id, selectedEventType)}
                          className="btn btn-primary"
                          style={{ width: '100%', marginBottom: '0.5rem' }}
                        >
                          ✓ Confirm Correction
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Assign to Player */}
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Link to Player:</strong>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {roster.map(player => (
                      <button
                        key={player.id}
                        onClick={() => handleLinkPlayer(currentEvent.id, player.id)}
                        className="btn btn-secondary"
                        style={{ fontSize: '0.875rem' }}
                        title={`Link event to ${player.name}`}
                      >
                        #{player.jersey_number} {player.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Delete Event */}
                <button
                  onClick={() => {
                    if (window.confirm('Delete this event? This cannot be undone.')) {
                      handleDeleteEvent(currentEvent.id);
                    }
                  }}
                  className="btn btn-danger"
                  style={{ width: '100%' }}
                >
                  🗑 Delete Event
                </button>
              </>
            )}
          </div>

          {/* Help Text */}
          <div className="card" style={{ marginTop: '1rem', background: '#eff6ff' }}>
            <h4 style={{ marginBottom: '0.5rem' }}>How to Review Events</h4>
            <div style={{ fontSize: '0.875rem', lineHeight: '1.8' }}>
              <div>1. Watch the event in the video player</div>
              <div>2. Correct the event type if AI prediction was wrong (optional reason)</div>
              <div>3. Link the event to the correct player</div>
              <div>4. Use Prev/Next buttons to navigate between events</div>
              <div>5. Delete events that were incorrectly detected</div>
              <div>6. View correction history to see all changes made</div>
            </div>
          </div>

          {/* Corrections History */}
          {showCorrectionsHistory && corrections.length > 0 && (
            <div className="card" style={{ marginTop: '1rem', background: '#f0fdf4' }}>
              <h4 style={{ marginBottom: '1rem' }}>Correction History</h4>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {corrections.map((correction, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '0.75rem',
                      background: '#f9fafb',
                      borderRadius: '4px',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      borderLeft: '3px solid #10b981'
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                      Event {correction.event_id}
                    </div>
                    <div>
                      Changed: <strong>{correction.original_type}</strong> → <strong>{correction.corrected_type}</strong>
                    </div>
                    {correction.reason && (
                      <div style={{ color: '#64748b', marginTop: '0.25rem' }}>
                        Reason: {correction.reason}
                      </div>
                    )}
                    <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      by {correction.corrected_by} on {new Date(correction.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
            </>
          )}

          {/* Errors Tab */}
          {activeTab === 'errors' && (
            <ErrorReviewPanel matchId={matchId} currentUser={null} />
          )}

          {/* Statistics Tab */}
          {activeTab === 'stats' && (
            <ErrorStatisticsPanel matchId={matchId} />
          )}
        </div>
      </div>
    </div>
  );
}

export default ReviewPage;
